/*!
   Copyright 2019 Ron Buckton

   Licensed under the Apache License, Version 2.0 (the "License");
   you may not use this file except in compliance with the License.
   You may obtain a copy of the License at

       http://www.apache.org/licenses/LICENSE-2.0

   Unless required by applicable law or agreed to in writing, software
   distributed under the License is distributed on an "AS IS" BASIS,
   WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
   See the License for the specific language governing permissions and
   limitations under the License.
*/
import { Duration } from './Duration';

export type Components<Unit extends string> = Partial<Record<Unit, number>>;
export type DateUnit = "year" | "month" | "day";
export type WeekDateUnit = "year" | "weekOfYear" | "dayOfWeek";
export type OrdinalDateUnit = "year" | "dayOfYear";
export type TimeUnit = "hour" | "minute" | "second" | "millisecond" | "nanosecond";
/**
 * Components with a nominal duration.
 */
export type DateDelta = "years" | "months" | "days";
/**
 * Components with an accurate duration.
 */
export type TimeDelta = "hours" | "minutes" | "seconds" | "milliseconds" | "nanoseconds";
export type InstantUnit = "milliseconds" | "nanoseconds";
export type InstantDelta = "milliseconds" | "nanoseconds";
export type TemporalDelta = DateDelta | TimeDelta | InstantDelta;
export type TemporalUnit = DateUnit | TimeUnit | InstantUnit;

export interface Temporal<T extends Temporal<T> = any> {
    plus(deltas?: Components<TemporalDelta>): T;
    minus(deltas?: Components<TemporalDelta>): T;
    with(components?: Components<TemporalUnit>): T;
    until(temporal: T): Duration;
    since(temporal: T): Duration;
    toString(): string;
    toISOString(): string;
    equals(other: T): boolean;
    compareTo(other: T): number;
}